import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Container,
  Card,
  Button,
  CardContent,
  Icon,
  CardActions,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { getAccessToken, useAuth } from "../../context/auth";
import Header, { HeaderType } from "../../components/Header";
import type { IMenuFormButton } from "../../services/model/formSection";
import { GetFormSectionMenu } from "../../services/api/formSections-service";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import { useConfirm } from "@/components/dialog/confirm";
import BrandLoader from "@/branding/BrandLoader";
import { BrandingContext } from "@/branding/BrandingProvider";

interface AdditionalServicesProps {
  style?: any;
  loading: boolean;
}

const AdditionalServices: React.FC<AdditionalServicesProps> = () => {
  const { authTokens } = useAuth();
  const token = getAccessToken(authTokens) ?? "";
  const [formMenuButtons, setFormMenuButton] = useState<IMenuFormButton[]>([]);
  const confirm = useConfirm();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { tenant } = useParams<{ tenant: string }>();
  const branding = useContext(BrandingContext);

  const fetchFormsButtons = async () => {
    setLoading(true);
    const response = await GetFormSectionMenu(token, branding!.clientId);
    if (response) {
      setFormMenuButton(response.data || []);
    } else {
      showDialog(
        "No Connection",
        "There seems to be a problem with your internet connection. Please check your connection and try again."
      );
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchFormsButtons();
  }, []);

  const routeToAccidentSupport = (
    formId: string,
    incidentType: string,
    formTitle: string,
    formDescription: string
  ) => {
    localStorage.setItem("formId", JSON.stringify(formId));
    localStorage.setItem("incidentType", JSON.stringify(incidentType));
    localStorage.setItem("formTitle", JSON.stringify(formTitle));
    localStorage.setItem("formDescription", JSON.stringify(formDescription));
    navigate(`/${tenant}/accidentsupportlist`);
  };

  const showDialog = (title: string, message: string) => {
    confirm({ catchOnCancel: false, title, description: message });
  };

  return (
    <div>
      <Header type={HeaderType.AdditionalServices} />
      <Container maxWidth="sm">
        <Box sx={{ p: 1 }}>
          {/* Responsive 2-up on small phones, 3-up from small screens */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" },
              gap: 2,
            }}
          >
            {formMenuButtons.map((btn) => (
              <Card
                key={btn.id}
                variant="outlined"
                sx={{ cursor: "pointer", height: "100%" }}
                onClick={() =>
                  routeToAccidentSupport(
                    btn.id,
                    btn.formType,
                    btn.title,
                    btn.description
                  )
                }
              >
                <CardContent sx={{ pt: 1, pb: 0, display: "flex", justifyContent: "center" }}>
                  <Icon color="primary" sx={{ fontSize: 44, lineHeight: 0 }}>
                    <FormatListBulletedIcon sx={{ width: "1em", height: "1em" }} />
                  </Icon>
                </CardContent>

                <CardActions sx={{ p: 1 }}>
                  <Button fullWidth color="secondary" sx={{ fontSize: 12, minHeight: 70 }}>
                    <strong>{btn.title}</strong>
                  </Button>
                </CardActions>
              </Card>
            ))}
          </Box>

          {formMenuButtons.length === 0 && !loading && (
            <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
              <Typography variant="body2">No services available.</Typography>
            </Box>
          )}
        </Box>
      </Container>
      <BrandLoader open={loading} />
    </div>
  );
};

export default AdditionalServices;
